import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import PatientLogin from "./pages/PatientLogin";
import StaffLogin from "./pages/StaffLogin";
import PatientDashboard from "./pages/PatientDashboard";
import PatientDoctors from "./pages/PatientDoctors";
import PatientDoctorProfile from "./pages/PatientDoctorProfile";
import PatientAppointments from "./pages/PatientAppointments";
import PatientAppointmentDetail from "./pages/PatientAppointmentDetail";
import PatientMedicalRecords from "./pages/PatientMedicalRecords";
import DoctorDashboard from "./pages/DoctorDashboard";
import DoctorAppointments from "./pages/DoctorAppointments";
import DoctorAvailability from "./pages/DoctorAvailability";
import ConsultationRoom from "./pages/ConsultationRoom";
import ClinicDashboard from "./pages/ClinicDashboard";
import ClinicAppointments from "./pages/ClinicAppointments";
import ClinicCheckups from "./pages/ClinicCheckups";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDoctors from "./pages/AdminDoctors";
import AdminClinics from "./pages/AdminClinics";
import AdminAssociations from "./pages/AdminAssociations";
import AdminPatients from "./pages/AdminPatients";
import AdminAppointments from "./pages/AdminAppointments";
import AdminReviews from "./pages/AdminReviews";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import AdminPayments from "./pages/AdminPayments";
import AdminDiagnostics from "./pages/AdminDiagnostics";
import AdminCategories from "./pages/AdminCategories";
import AdminBanners from "./pages/AdminBanners";
import AdminBlogs from "./pages/AdminBlogs";
import AdminIntegrations from "./pages/AdminIntegrations";
import PatientBlogs from "./pages/PatientBlogs";
import PatientBlogPost from "./pages/PatientBlogPost";

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false && user.role) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/patient/login" element={<PublicOnly><PatientLogin /></PublicOnly>} />
      <Route path="/staff/login" element={<PublicOnly><StaffLogin /></PublicOnly>} />

      <Route path="/patient/dashboard" element={<ProtectedRoute role="patient"><PatientDashboard /></ProtectedRoute>} />
      <Route path="/patient/doctors" element={<ProtectedRoute role="patient"><PatientDoctors /></ProtectedRoute>} />
      <Route path="/patient/doctors/:id" element={<ProtectedRoute role="patient"><PatientDoctorProfile /></ProtectedRoute>} />
      <Route path="/patient/appointments" element={<ProtectedRoute role="patient"><PatientAppointments /></ProtectedRoute>} />
      <Route path="/patient/appointments/:id" element={<ProtectedRoute role="patient"><PatientAppointmentDetail /></ProtectedRoute>} />
      <Route path="/patient/medical-records" element={<ProtectedRoute role="patient"><PatientMedicalRecords /></ProtectedRoute>} />
      <Route path="/patient/consult/:appointmentId" element={<ProtectedRoute role="patient"><ConsultationRoom mode="patient" /></ProtectedRoute>} />

      <Route path="/doctor/dashboard" element={<ProtectedRoute role="doctor"><DoctorDashboard /></ProtectedRoute>} />
      <Route path="/doctor/appointments" element={<ProtectedRoute role="doctor"><DoctorAppointments /></ProtectedRoute>} />
      <Route path="/doctor/availability" element={<ProtectedRoute role="doctor"><DoctorAvailability /></ProtectedRoute>} />
      <Route path="/doctor/consult/:appointmentId" element={<ProtectedRoute role="doctor"><ConsultationRoom mode="doctor" /></ProtectedRoute>} />

      <Route path="/clinic/dashboard" element={<ProtectedRoute role="clinic"><ClinicDashboard /></ProtectedRoute>} />
      <Route path="/clinic/appointments" element={<ProtectedRoute role="clinic"><ClinicAppointments /></ProtectedRoute>} />
      <Route path="/clinic/appointments/:id/checkups" element={<ProtectedRoute role="clinic"><ClinicCheckups /></ProtectedRoute>} />
      <Route path="/clinic/consult/:appointmentId" element={<ProtectedRoute role="clinic"><ConsultationRoom mode="clinic" /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/doctors" element={<ProtectedRoute role="admin"><AdminDoctors /></ProtectedRoute>} />
      <Route path="/admin/clinics" element={<ProtectedRoute role="admin"><AdminClinics /></ProtectedRoute>} />
      <Route path="/admin/associations" element={<ProtectedRoute role="admin"><AdminAssociations /></ProtectedRoute>} />
      <Route path="/admin/patients" element={<ProtectedRoute role="admin"><AdminPatients /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute role="admin"><AdminAppointments /></ProtectedRoute>} />
      <Route path="/admin/reviews" element={<ProtectedRoute role="admin"><AdminReviews /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute role="admin"><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute role="admin"><AdminPayments /></ProtectedRoute>} />
      <Route path="/admin/diagnostics" element={<ProtectedRoute role="admin"><AdminDiagnostics /></ProtectedRoute>} />
      <Route path="/admin/content/categories" element={<ProtectedRoute role="admin"><AdminCategories /></ProtectedRoute>} />
      <Route path="/admin/content/banners" element={<ProtectedRoute role="admin"><AdminBanners /></ProtectedRoute>} />
      <Route path="/admin/content/blogs" element={<ProtectedRoute role="admin"><AdminBlogs /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute role="admin"><AdminIntegrations /></ProtectedRoute>} />
      <Route path="/patient/blogs" element={<ProtectedRoute role="patient"><PatientBlogs /></ProtectedRoute>} />
      <Route path="/patient/blogs/:slug" element={<ProtectedRoute role="patient"><PatientBlogPost /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              classNames: {
                toast:
                  "bg-white border border-border text-foreground rounded-xl shadow-lg",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
